import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { createClient } from '@supabase/supabase-js';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { Analytics } from '@vercel/analytics/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import '@n8n/chat/style.css';
import './chatbot-styles.css';
import { createChat } from '@n8n/chat';
import LoadingOverlay from './components/LoadingOverlay';
import Footer from './components/Footer';
import MinimalBetfairTest from './components/debug/MinimalBetfairTest';
import Landing from './pages/Landing';
import LandingSystem from './pages/LandingSystem';
import FreeTrial from './pages/FreeTrial';
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import DashboardSystem from "./pages/DashboardSystem";
import RefundPolicy from "./pages/RefundPolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import Affiliates from "./pages/Affiliates";
import FAQs from "./pages/FAQs";
import Contact from "./pages/Contact";
import LandingBetfair from './pages/LandingBetfair';
import LandingGoalProfits from './pages/LandingGoalProfits';
import TrialConfirmed from './pages/TrialConfirmed';
import SubscriberConfirmed from './pages/SubscriberConfirmed';
import LandingRatedTipsters from './pages/LandingRatedTipsters';

// System pages
import SystemFAQs from './pages/system/FAQs';
import SystemPrivacyPolicy from './pages/system/PrivacyPolicy';
import SystemTermsConditions from './pages/system/TermsConditions';
import SystemRefundPolicy from './pages/system/RefundPolicy';
import SystemAffiliates from './pages/system/Affiliates';
import SystemContact from './pages/system/Contact';
import SystemThankYou from './pages/system/ThankYou';
import SystemRegister from './pages/system/Register';
import SystemEmailConfirmed from './pages/system/EmailConfirmed';
import SystemUserGuide from './pages/system/UserGuide';
import MaintenancePage from './pages/MaintenancePage';
import ProtectedRoute from './components/common/ProtectedRoute';

// Lazy load components
const ApiKeyCallback = React.lazy(() => import('./pages/ApiKeyCallback'));
const BetfairAuthPage = React.lazy(() => import('./pages/BetfairAuthPage'));

// Lazy load the new dashboard
const NewDashboard = React.lazy(() => import('./pages/NewDashboard'));

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
);

// MAINTENANCE_MODE bypass: Use ?access_key=letmein2024 in the URL to bypass maintenance lockout for yourself.
const MAINTENANCE_MODE = false; // Set to true to enable global maintenance mode
const MAINTENANCE_BYPASS_KEY = 'maniac1244';

function hasBypassKey() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    return params.get('access_key') === MAINTENANCE_BYPASS_KEY;
  }
  return false;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return user ? <>{children}</> : <Navigate to="/login" />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (location.pathname === '/reset-password') {
    return <>{children}</>;
  }
  
  const isAppDomain = window.location.hostname === 'app.horseracingoracle.com';
  return !user ? <>{children}</> : <Navigate to={isAppDomain ? "/dashboard/beta" : "/dashboard/system"} />;
}

function AppRoutes() {
  // Check if we're on the app subdomain
  const isAppDomain = window.location.hostname === 'app.horseracingoracle.com';
  const isSystemDomain = !isAppDomain;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow">
        <React.Suspense fallback={<LoadingOverlay />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={isAppDomain ? <Landing /> : <LandingSystem />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            {/* Rated Tipsters Landing Page */}
            {isAppDomain && <Route path="/ratedtipsters" element={<LandingRatedTipsters />} />}
            
            {/* Hidden beta dashboard route */}
            <Route
              path="/dashboard/beta/*"
              element={
                <ProtectedRoute>
                  <PrivateRoute>
                    <NewDashboard />
                  </PrivateRoute>
                </ProtectedRoute>
              }
            />

            {/* System-specific routes */}
            <Route path="/system/faqs" element={<SystemFAQs />} />
            <Route path="/system/privacy-policy" element={<SystemPrivacyPolicy />} />
            <Route path="/system/terms-conditions" element={<SystemTermsConditions />} />
            <Route path="/system/refund-policy" element={<SystemRefundPolicy />} />
            <Route path="/system/affiliates" element={<SystemAffiliates />} />
            <Route path="/system/contact" element={<SystemContact />} />
            <Route path="/system/thank-you" element={<SystemThankYou />} />
            <Route path="/system/register" element={<SystemRegister />} />
            <Route path="/system/auth/confirm-email" element={<SystemEmailConfirmed />} />

            {/* Original routes */}
            <Route path="/free-trial" element={<FreeTrial />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/affiliates" element={<Affiliates />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth/confirm-email" element={<SystemEmailConfirmed />} />
            <Route
              path="/login"
              element={
                <ProtectedRoute>
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />

            {/* Protected routes - order matters here */}
            <Route
              path="/dashboard/system/*"
              element={
                <ProtectedRoute>
                  <PrivateRoute>
                    {!isAppDomain ? (
                      <Routes>
                        <Route path="/" element={<DashboardSystem />} />
                        <Route path="/user-guide" element={<SystemUserGuide />} />
                      </Routes>
                    ) : (
                      <Navigate to="/dashboard/beta" />
                    )}
                  </PrivateRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <PrivateRoute>
                    <Navigate to="/dashboard/beta" replace />
                  </PrivateRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auth/betfair"
              element={
                <ProtectedRoute>
                  <PrivateRoute>
                    <BetfairAuthPage />
                  </PrivateRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/api/auth/callback/betfair"
              element={
                <PrivateRoute>
                  <ApiKeyCallback />
                </PrivateRoute>
              }
            />
            <Route path="/debug/oauth" element={<MinimalBetfairTest />} />
            <Route path="/betfair" element={<LandingBetfair />} />
            <Route path="/goalprofits" element={<LandingGoalProfits />} />
            <Route path="/trial-confirmed" element={<TrialConfirmed />} />
            <Route path="/subscriber-confirmed" element={<SubscriberConfirmed />} />
          </Routes>
        </React.Suspense>
      </div>
      {/* Only show the old Footer on non-system pages */}
      {!isSystemDomain && <Footer />}
    </div>
  );
}

function App() {
  useEffect(() => {
    createChat({
      webhookUrl: 'https://n8n.alba-nova.com/webhook/91d02920-0317-49fe-bc20-388c03b4beea/chat',
      initialMessages: [
        "I'm Magnum, your AI assistant. How can I help?"
      ],
      i18n: {
        en: {
          title: "I'm Magnum 🐎",
          subtitle: "I'm happy to answer any questions about Horse Racing Oracle",
          inputPlaceholder: 'Type your question..',
          footer: '',
          getStarted: 'New Conversation',
          closeButtonTooltip: 'Close chat',
        },
      },
    });
  }, []);

  // Global maintenance mode: show MaintenancePage for all routes unless bypass key is present
  if (MAINTENANCE_MODE && !hasBypassKey()) {
    return <MaintenancePage />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <SessionContextProvider supabaseClient={supabase}>
          <AuthProvider>
            <AppRoutes />
            <Analytics />
          </AuthProvider>
        </SessionContextProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
